import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Select } from '@/components/ui/Select';
import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { formatMoney } from '@/lib/money';

import styles from './TenantsPage.module.scss';
import {
  listTenants,
  type TenantEdge,
  type TenantLifecycleOption,
  type TenantListSort,
  type TenantOnboardingFilter,
  type TenantsConnection,
  type TenantStatusResult,
} from '../api/tenants.graphql';
import { CreateTenantWizard } from '../components/CreateTenantWizard';
import { ExpiryAlertChip } from '../components/ExpiryAlertChip';
import { OnboardingProgress } from '../components/OnboardingProgress';
import { TenantStatusDialog } from '../components/TenantStatusDialog';
import { PLAN_TIER_OPTIONS } from '../select-options';
import {
  LIFECYCLE_ACTIONS,
  ONBOARDING_FILTER_OPTIONS,
  PLAN_STATUS_ORDER,
  planLabel,
  statusLabel,
} from '../tenant-labels';

export const TENANTS_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const SORT_OPTIONS: { value: TenantListSort; label: string }[] = [
  { value: 'createdAt_desc', label: 'Más recientes' },
  { value: 'createdAt_asc', label: 'Más antiguos' },
  { value: 'name_asc', label: 'Nombre (A–Z)' },
  { value: 'name_desc', label: 'Nombre (Z–A)' },
];
const SORT_VALUES = new Set<string>(SORT_OPTIONS.map((o) => o.value));

function isOnboardingFilter(value: string): value is TenantOnboardingFilter {
  return ONBOARDING_FILTER_OPTIONS.some((option) => option.value === value);
}

function PlanBadge({ tier }: { tier: string }): JSX.Element {
  return <span className={`${styles.badge} ${styles[tier] ?? ''}`}>{planLabel(tier)}</span>;
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  return <span className={`${styles.badge} ${styles[status] ?? ''}`}>{statusLabel(status)}</span>;
}

/** Une las páginas sin duplicar: si un tenant aparece en dos páginas, queda la primera. */
export function mergeTenantPages(pages: TenantsConnection[]): TenantEdge[] {
  const seen = new Set<string>();
  const merged: TenantEdge[] = [];
  for (const page of pages) {
    for (const edge of page.edges) {
      if (!seen.has(edge.id)) {
        seen.add(edge.id);
        merged.push(edge);
      }
    }
  }
  return merged;
}

export function TenantsPage(): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // OS-1717 — acciones según las capacidades del rol (el API las vuelve a exigir).
  const { can, canAll } = usePlatformPermissions();
  const canCreate = canAll('platform.tenants.create', 'platform.modules.write');
  const canEdit = can('platform.tenants.update') || can('platform.tenants.configure');
  const canChangeStatus = can('platform.tenants.suspend');
  const showMrr = can('platform.billing.read');
  const columns = showMrr ? 8 : 7;
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const statusFilter = params.get('status') ?? '';
  const tierFilter = params.get('plan') ?? '';
  // OS-1718 — "Onboarding incompleto" / "Onboarding estancado" (enlazable desde el dashboard).
  const rawOnboarding = params.get('onboarding') ?? '';
  const onboardingFilter = isOnboardingFilter(rawOnboarding) ? rawOnboarding : undefined;
  const rawSort = params.get('sort') ?? '';
  const sort: TenantListSort = SORT_VALUES.has(rawSort)
    ? (rawSort as TenantListSort)
    : 'createdAt_desc';

  const [searchInput, setSearchInput] = useState(search);
  const [showWizard, setShowWizard] = useState(false);
  const [statusChange, setStatusChange] = useState<{
    tenant: TenantEdge;
    option: TenantLifecycleOption;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const setParam = useCallback(
    (key: string, value: string): void => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // La búsqueda va a la URL con debounce: no se consulta el API en cada tecla.
  useEffect(() => {
    if (searchInput.trim() === search) {
      return undefined;
    }
    const timer = setTimeout(() => setParam('q', searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return (): void => clearTimeout(timer);
  }, [searchInput, search, setParam]);

  const query = useInfiniteQuery({
    queryKey: [
      'platform-tenants',
      { search, planStatus: statusFilter, planTier: tierFilter, onboarding: onboardingFilter, sort },
    ],
    queryFn: ({ pageParam }) =>
      listTenants({
        search: search || undefined,
        planStatus: statusFilter || undefined,
        planTier: tierFilter || undefined,
        onboarding: onboardingFilter,
        sort,
        cursor: pageParam,
        take: TENANTS_PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasNextPage ? last.nextCursor : undefined),
  });

  const tenants = useMemo(() => mergeTenantPages(query.data?.pages ?? []), [query.data]);

  function applyStatusResult(result: TenantStatusResult): void {
    // Actualiza la fila en su lugar: sin recargar la lista ni perder las páginas ya cargadas.
    qc.setQueriesData<InfiniteData<TenantsConnection>>(
      { queryKey: ['platform-tenants'] },
      (data) =>
        data && {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            edges: page.edges.map((edge) =>
              edge.id === result.id
                ? {
                    ...edge,
                    planStatus: result.planStatus,
                    isActive: result.isActive,
                    allowedStatusTransitions: result.allowedStatusTransitions,
                    pilotStartsAt: result.pilotStartsAt,
                    pilotEndsAt: result.pilotEndsAt,
                    convertedAt: result.convertedAt,
                    cancelledAt: result.cancelledAt,
                    expiryAlert: result.expiryAlert,
                    allowedLifecycleActions: result.allowedLifecycleActions,
                  }
                : edge,
            ),
          })),
        },
    );
    void qc.invalidateQueries({ queryKey: ['platform-tenant', result.id] });
    // OS-1718 — el estancamiento depende del estado (suspendido o cancelado no cuenta): se marca
    // desactualizado sin recargar ahora, para no pisar la fila recién actualizada.
    void qc.invalidateQueries({ queryKey: ['platform-tenants'], refetchType: 'none' });
    setNotice(
      `${result.name}: estado actualizado a ${statusLabel(result.planStatus).toLowerCase()}.`,
    );
    setStatusChange(null);
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <input
          type="search"
          aria-label="Buscar tenants"
          className={styles.searchInput}
          placeholder="Buscar por nombre, email, subdominio o NIT…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Select
          aria-label="Filtrar por estado"
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(value) => setParam('status', value)}
          options={[
            { value: '', label: 'Todos los estados' },
            ...PLAN_STATUS_ORDER.map((status) => ({ value: status, label: statusLabel(status) })),
          ]}
        />
        <Select
          aria-label="Filtrar por plan"
          className={styles.filterSelect}
          value={tierFilter}
          onChange={(value) => setParam('plan', value)}
          options={[{ value: '', label: 'Todos los planes' }, ...PLAN_TIER_OPTIONS]}
        />
        <Select
          aria-label="Filtrar por onboarding"
          className={styles.filterSelect}
          value={onboardingFilter ?? ''}
          onChange={(value) => setParam('onboarding', value)}
          options={[{ value: '', label: 'Todo el onboarding' }, ...ONBOARDING_FILTER_OPTIONS]}
        />
        <Select
          aria-label="Ordenar"
          className={styles.filterSelect}
          value={sort}
          onChange={(value) => setParam('sort', value === 'createdAt_desc' ? '' : value)}
          options={SORT_OPTIONS}
        />
        {canCreate ? (
          <button type="button" className={styles.createBtn} onClick={() => setShowWizard(true)}>
            <Plus size={16} aria-hidden="true" /> Nuevo tenant
          </button>
        ) : null}
      </div>

      <p className={styles.notice} role="status">
        {notice}
      </p>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>Tenants</caption>
          <thead>
            <tr>
              <th scope="col">Empresa</th>
              <th scope="col">Subdominio</th>
              <th scope="col">Plan</th>
              <th scope="col">Estado</th>
              <th scope="col">Onboarding</th>
              {showMrr ? <th scope="col">MRR (COP)</th> : null}
              <th scope="col">Alta</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr>
                <td colSpan={columns} className={styles.emptyState}>
                  <span role="status">Cargando…</span>
                </td>
              </tr>
            )}
            {query.isError && !query.data && (
              <tr>
                <td colSpan={columns} className={styles.emptyState}>
                  {/* OS-1725 C3 — el rol va en el contenido: en la celda le quitaba su semántica. */}
                  <span role="alert">Error cargando tenants: {query.error.message}</span>
                </td>
              </tr>
            )}
            {query.isSuccess && tenants.length === 0 && (
              <tr>
                <td colSpan={columns} className={styles.emptyState}>
                  No se encontraron tenants
                </td>
              </tr>
            )}
            {tenants.map((t) => (
              <tr key={t.id}>
                <td data-label="Empresa">
                  <div className={styles.tenantName}>{t.name}</div>
                  {t.email && <div className={styles.subdomain}>{t.email}</div>}
                </td>
                <td data-label="Subdominio">
                  <span className={styles.subdomain}>
                    {t.subdomain ? `${t.subdomain}.opssynk.com` : '—'}
                  </span>
                </td>
                <td data-label="Plan">
                  <PlanBadge tier={t.planTier} />
                </td>
                <td data-label="Estado">
                  <div className={styles.statusCell}>
                    <StatusBadge status={t.planStatus} />
                    {t.expiryAlert ? <ExpiryAlertChip alert={t.expiryAlert} /> : null}
                  </div>
                </td>
                <td data-label="Onboarding">
                  {t.onboarding ? (
                    <OnboardingProgress onboarding={t.onboarding} label={`Onboarding de ${t.name}`} />
                  ) : (
                    '—'
                  )}
                </td>
                {showMrr ? (
                  <td data-label="MRR">
                    {/* OS-1723 — MRR real calculado; en piloto se muestra el potencial. */}
                    {typeof t.potentialMrr === 'number' && t.potentialMrr > 0
                      ? `${formatMoney(t.potentialMrr)} potencial`
                      : formatMoney(t.mrr)}
                  </td>
                ) : null}
                <td data-label="Alta">{new Date(t.createdAt).toLocaleDateString('es-CO')}</td>
                <td data-label="Acciones">
                  <div className={styles.actions}>
                    <button
                      type="button"
                      className={styles.actionBtn}
                      aria-label={`${canEdit ? 'Editar' : 'Ver'} ${t.name}`}
                      onClick={() => void navigate(`/tenants/${t.id}`)}
                    >
                      {canEdit ? 'Editar' : 'Ver'}
                    </button>
                    {(canChangeStatus ? t.allowedLifecycleActions : []).map((option) => (
                      <button
                        key={option.action}
                        type="button"
                        className={`${styles.actionBtn} ${LIFECYCLE_ACTIONS[option.action].danger ? styles.danger : ''}`}
                        aria-label={`${LIFECYCLE_ACTIONS[option.action].label} ${t.name}`}
                        onClick={() => {
                          setNotice(null);
                          setStatusChange({ tenant: t, option });
                        }}
                      >
                        {LIFECYCLE_ACTIONS[option.action].label}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {query.isFetchNextPageError ? (
          <p className={styles.emptyState} role="alert">
            No se pudo cargar la página siguiente: {query.error?.message}
          </p>
        ) : null}
        {query.hasNextPage && (
          <div className={styles.loadMore}>
            <button
              type="button"
              onClick={() => void query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
            >
              {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </button>
          </div>
        )}
        {tenants.length > 0 ? (
          <p className={styles.count}>
            Mostrando {tenants.length} {tenants.length === 1 ? 'tenant' : 'tenants'}
            {query.hasNextPage ? ' (hay más)' : ''}
          </p>
        ) : null}
      </div>

      {statusChange && (
        <TenantStatusDialog
          tenant={statusChange.tenant}
          option={statusChange.option}
          onDone={applyStatusResult}
          onCancel={() => setStatusChange(null)}
        />
      )}

      {showWizard && (
        <CreateTenantWizard
          onClose={() => setShowWizard(false)}
          onCreated={() => {
            setShowWizard(false);
            void qc.invalidateQueries({ queryKey: ['platform-tenants'] });
          }}
        />
      )}
    </div>
  );
}
